<script>

	import { onMount } from 'svelte';
	import { fade } from "svelte/transition";

	import * as Sentry from "@sentry/svelte";

	import { Room, RoomEvent, setLogLevel, setLogExtension } from 'livekit-client';

	import Stage from "./Stage.svelte";
	// import HiddenParticipant from "./HiddenParticipant.svelte";

	import { colord } from "colord";

	import { getServerData, postServerData } from './lib/prelude.js';
	import { randomIntFromInterval } from './lib/utils.js';

	import {
		accessToken,
		event,
		session,
		attendees,
		syncClient,
		syncChannels,
		participants,
		layout,
		banners,
		backgrounds,
		overlays,
		clips,
		clipTokens,
		banner,
		background,
		overlay,
		clip,
		highlight,
		participantCount,
		room,
		trackPublications,
		broadcasting,
		livestreamStatus
	} from './lib/stores.js';

	let mounted = false;
	let twilioSyncLoaded = false;
	let rtcClientLoaded = false;
	let playerxLoaded = false;
	let mediachromeLoaded = false;
	let muxvideoLoaded = false;
	let connected = false;

	let syncToken;
	let roomName;
	let roomToken;
	let studioChannel;
	let sessionChannel;

	let livekitHost;

	let slate;

	let resubscribes = {};

	let statusTimer;

	onMount(async () => {

		let hash = location.hash;
		let hashItems = hash.split('/');
		$accessToken = hashItems[1];

		if ($accessToken) {

			let accessData = await getServerData('virtual/studio/webinput');

			if (accessData) {

				syncToken = accessData.syncToken;

				$session = accessData.session;
				$syncChannels = accessData.syncChannels;

				livekitHost = accessData.livekitHost;

				rtcClientLoaded = true;

				setEvent(accessData.event);

				const sessionData = await getServerData('virtual/session', {
					session: $session.ref,
					studio: 1
				});

				if (sessionData) {

					$session.syncChannel = sessionData.syncChannel;
					$session.controlChannel = sessionData.controlChannel;
					$session.studioChannel = sessionData.studioChannel;
					$session.controlChannel = sessionData.controlChannel;
					$session.livestream = sessionData.livestream;

					if (sessionData.room) {
						roomName = sessionData.room;
					}

					if (sessionData.roomToken) {
						roomToken = sessionData.roomToken;
					}

					$backgrounds = sessionData.backgrounds;
					$banners = sessionData.banners;
					$overlays = sessionData.overlays;
					$clips = sessionData.clips;

					$livestreamStatus = sessionData.livestreamStatus;

					getClipTokens(true);

					$session.pollResponses = sessionData.pollResponses ? sessionData.pollResponses : {};

					mounted = true;

					// tmp!
					const j = JSON.stringify($session);
					postServerData('virtual/studio/debug', {
						sessionRef: $session.ref,
						json: j
					});

					setStatusTimer();

				}

			}

		}

	});

	function setStatusTimer() {
		clearTimeout(statusTimer);
		if ($livestreamStatus !== 'active') {
			statusTimer = setTimeout(() => {
				getServerData('virtual/studio/status', {
					livestream: $session.livestream
				});
			}, 10000);
		}
	}

	function setEvent(eventData) {

		$event = eventData;

		document.title = $event.name;

		['textColor','panelColor','accentColor','captionColor','buttonTextColor','bgColor','logoColor'].forEach(c => {
			if ($event.theme.virtual[c]) document.body.style.setProperty('--' + c, $event.theme.colors[$event.theme.virtual[c]]);
		});

		['05','10','20','40','60','80','90','120'].forEach(b => {
			if ($event.theme.virtual['blend-' + b]) document.body.style.setProperty('--blend-' + b, $event.theme.virtual['blend-' + b]);
			if ($event.theme.virtual['accentBlend-' + b]) document.body.style.setProperty('--accentBlend-' + b, $event.theme.virtual['accentBlend-' + b]);
		});

		['red','red-120','bgBlur','bgOpacity','bgScale'].forEach(s => {
			if ($event.theme.virtual[s]) document.body.style.setProperty('--' + s, $event.theme.virtual[s]);
		});

		let tc = $event.theme.colors[$event.theme.virtual.textColor];
		let hsl = colord(tc).toHsl();
		hsl.l = '15';
		let shadow = colord(hsl).alpha(0.4).toRgbString();
		document.body.style.setProperty('--shadow', shadow);

	}

	function setTwilioSyncLoaded() {
		twilioSyncLoaded = true;
	}

	function setRtcClientLoaded() {
		rtcClientLoaded = true;
	}

	function scriptLoadError(scriptName) {
		// 24 Aug 2026 -- these <svelte:head> scripts can fail to load
		// (CDN issue, network blip, etc.) before $session is populated by
		// onMount, so only include sessionRef in the debug report when
		// it's actually available -- Sentry capture happens either way.
		const message = `Studio egress: failed to load ${scriptName}`;
		Sentry.captureMessage(message, 'error');
		if ($session) {
			postServerData('virtual/studio/debug', {
				sessionRef: $session.ref,
				message: message
			});
		}
	}

	async function refreshSyncToken() {
		const refreshData = await getServerData('virtual/studio/webinput');
		if (refreshData.syncToken) {
			syncToken = refreshData.syncToken;
			$syncClient.updateToken(syncToken);
		}
	}

	async function twilioSyncConnect() {
		if (!$syncClient) {

			$syncClient = new Twilio.Sync.Client(syncToken);

			$syncClient.on('connectionError', (connectionError) => {
				refreshSyncToken();
			});

			$syncClient.on('tokenAboutToExpire', () => {
				refreshSyncToken();
			});

			try {

				let eventNotifications = await $syncClient.list($syncChannels.event);
				eventNotifications.on('itemAdded', syncItemAdded);

			} catch (e) {

				console.log("Sync error", e, $syncChannels.event);
				const j = JSON.stringify({
					channel: $syncChannels.event,
					err: e
				});
				postServerData('virtual/studio/debug', {
					sessionRef: $session.ref,
					message: "Sync error",
					json: j
				});

			}

			try {

				let attendance = await $syncClient.map($syncChannels.attendance);

				attendance.on('itemAdded', (args) => {
					$attendees[args.item.key] = args.item.data;
					$attendees[args.item.key].seq = $attendees[args.item.key].seq ? $attendees[args.item.key].seq : args.item.dateUpdated.getTime();
					$attendees = $attendees;
				});

				attendance.on('itemRemoved', (args) => {
					delete $attendees[args.key];
					$attendees = $attendees;
				});

				attendance.on('itemUpdated', (args) => {
					$attendees[args.item.key] = args.item.data;
					$attendees = $attendees;
				});

				getAttendance(attendance);

			} catch (e) {

				console.log("Sync error", e, $syncChannels.attendance);
				const j = JSON.stringify({
					channel: $syncChannels.attendance,
					err: e
				});
				postServerData('virtual/studio/debug', {
					sessionRef: $session.ref,
					message: "Sync error",
					json: j
				});

			}

		}
	}

	async function connectSessionChannels() {
		if ($session.studioChannel) {

			studioChannel = await $syncClient.document($session.studioChannel);
			setStoresFromSync(studioChannel.data);

			studioChannel.on('updated', (args) => {
				setStoresFromSync(args.data);
			});

		}

		if ($session.syncChannel) {

			sessionChannel = await $syncClient.list($session.syncChannel);

			sessionChannel.on('itemAdded', function(i) {
				if (i.item.data.type == 'studio') {
					refreshLibrary(i.item.data.lib);
				}
			});

		}
	}

	function attendeesPageHandler(paginator) {
		paginator.items.forEach((item) => {
			$attendees[item.key] = item.data;
			$attendees[item.key].seq = $attendees[item.key].seq ? $attendees[item.key].seq : item.dateUpdated.getTime();
		});
		// console.log('attendees',attendees);
		$attendees = $attendees;
		return paginator.hasNextPage
			? paginator.nextPage().then(attendeesPageHandler)
			: null;	}

	function getAttendance(attendance) {
		attendance.getItems({ pageSize: 100 })
			.then(attendeesPageHandler)
			.catch((error) => {
				if (error.message.match('Maximum attempt')) {
					// back off and try again later...
					const smear = 1000 * randomIntFromInterval(5,10);
					setTimeout(() => {
						getAttendance(attendance);
					}, smear);
				} else {
					console.error('getAttendance failed', error);
				}
			});
	}

	async function syncItemAdded(message) {
		const messageData = message.item.data;
		if (messageData && messageData.type && (messageData.type == 'system')) {
			if (messageData.hook && (messageData.hook == 'settings')) {
				const eventData = await getServerData('virtual/auth/settings');
				if (eventData.event) setEvent(eventData.event);
			}
		} else if (messageData && messageData.type && (messageData.type == 'livestream')) {
			if (messageData.stream && (messageData.stream == $session.livestream) && messageData.status) {
				$livestreamStatus = messageData.status;
			}
		} else if (messageData && messageData.type && (messageData.type == 'session-reload')) {
			if (messageData.sessionRef && (messageData.sessionRef == $session.ref)) {
				window.location.reload();
			}
		}
	}

	async function loadRoom() {

		// setLogLevel('debug');

		setLogExtension((level, msg, context) => {
			// Sentry.captureMessage(`${msg}:: ${JSON.stringify(context)}`, level);
			// if ((msg == 'received server offer') || (msg == 'sending answer')) {
			// 	const j = JSON.stringify(context);
			// 	postServerData('virtual/studio/debug', {
			// 		sessionRef: $session.ref,
			// 		message: msg,
			// 		json: j
			// 	});
			// }
			if (level == 4) {
				const j = JSON.stringify(context);
				postServerData('virtual/studio/debug', {
					sessionRef: $session.ref,
					message: msg,
					json: j
				});
			}
		});

		// 24 Aug 2026 -- adaptiveStream pauses/delays video delivery for
		// tracks whose attached element isn't yet reported as visible/
		// correctly sized by ResizeObserver/IntersectionObserver (a real
		// client->server round trip, not just local rendering). That
		// exists to save a *viewer's* bandwidth on tracks they're not
		// looking at -- it doesn't apply here, since every track this
		// client subscribes to is, by definition, part of the composited
		// recording. It was very likely the main cause of the pronounced
		// screenshare delay: a newly-added slot's CSS transition (Stage's
		// in:fadeScale) plus this client's much heavier concurrent CPU
		// load (rendering + encoding + RTMP output simultaneously) means
		// those observer callbacks report a stable/visible state far
		// slower here than in a comparatively idle regular participant's
		// browser, even though the code and this setting are identical in
		// both apps.
		$room = new Room({
			adaptiveStream: false,
			dynacast: true
		});

		await $room.connect('wss://' + livekitHost, roomToken, {
			autoSubscribe: true
		});

		let roomSid = await $room.getSid();

		postServerData('virtual/studio/debug', {
			sessionRef: $session.ref,
			message: 'Studio egress connected (' + roomSid + ')'
		});

		$room.on(RoomEvent.TrackSubscriptionFailed, (sid, participant, err) => {
			let pub;
			participant.trackPublications.forEach((publication) => {
				if (publication.trackSid == sid) {
					pub = publication;
				}
			});
			const j = JSON.stringify({
				sid: sid,
				err: err,
				pub: pub
			});
			postServerData('virtual/studio/debug', {
				sessionRef: $session.ref,
				message: 'TrackSubscriptionFailed',
				json: j
			});
		});

		// $room.on(RoomEvent.ParticipantConnected, (participant) => {
		// 	setTrackPublications();
		// 	// const j = JSON.stringify(participant);
		// 	// postServerData('virtual/studio/debug', {
		// 	// 	sessionRef: $session.ref,
		// 	// 	message: 'ParticipantConnected',
		// 	// 	json: j
		// 	// });
		// });

		$room.on(RoomEvent.Reconnected, (e) => {
			setTrackPublications();
		});

		$room.on(RoomEvent.TrackSubscribed, (e) => {
			setTrackPublications();
			// if (e.kind == 'audio') {
				// const j = JSON.stringify(e);
				// postServerData('virtual/studio/debug', {
				// 	sessionRef: $session.ref,
				// 	message: 'Subscribed to track',
				// 	json: j
				// });
			// }
		});

		$room.on(RoomEvent.TrackUnsubscribed, (e) => {
			setTrackPublications();
			// if (e.kind == 'audio') {
			// 	const j = JSON.stringify(e);
			// 	postServerData('virtual/studio/debug', {
			// 		sessionRef: $session.ref,
			// 		message: 'TrackUnsubscribed',
			// 		json: j
			// 	});
			// }
		});

		// $room.on(RoomEvent.TrackPublished, (p) => {
		// 	// setTrackPublications();
		// 	if (p.kind == 'audio') {
		// 		const j = JSON.stringify(p);
		// 		postServerData('virtual/studio/debug', {
		// 			sessionRef: $session.ref,
		// 			message: 'TrackPublished',
		// 			json: j
		// 		});
		// 	}
		// });

		// $room.on(RoomEvent.TrackPublished, (p) => {
		// 	p.setSubscribed(true);
		// 	// if (p.trackName && (p.trackName == 'dummy')) {
		// 	// 	setTimeout(() => {
		// 	// 		let msg = p.isSubscribed ? 'Subscribed to dummy track' : 'Did not subscribe to dummy track';
		// 	// 		postServerData('virtual/studio/debug', {
		// 	// 			sessionRef: $session.ref,
		// 	// 			message: msg
		// 	// 		});
		// 	// 		if (p.isSubscribed) {
		// 	// 			p.setSubscribed(false);
		// 	// 			setTimeout(() => {
		// 	// 				p.setSubscribed(true);
		// 	// 			}, 1000);
		// 	// 		}
		// 	// 	}, 1000);
		// 	// }
		// });

		// // Also subscribe to tracks published before participant joined
		// $room.remoteParticipants.forEach((participant) => {
		// 	participant.trackPublications.forEach((publication) => {
		// 		publication.setSubscribed(true);
		// 	});
		// });

		$room.on(RoomEvent.TrackUnpublished, (e) => {
			setTrackPublications();
		});

		$room.on(RoomEvent.ParticipantDisconnected, (e) => {
			setTrackPublications();
		});

		$room.on(RoomEvent.TrackMuted, (e) => {
			setTrackPublications();
		});

		$room.on(RoomEvent.TrackUnmuted, (e) => {
			setTrackPublications();
		});

		$room.on(RoomEvent.TrackStreamStateChanged, (p,s) => {
			const j = JSON.stringify({
				RemoteTrackPublication: p,
				streamState: s
			});
			postServerData('virtual/studio/debug', {
				sessionRef: $session.ref,
				message: 'TrackStreamStateChanged',
				json: j
			});
		});

		setTimeout(() => {
			setTrackPublications();
			connected = true;
		}, 400);

	}

	let trackPublicationsDebouncer;

	function setTrackPublications() {
		clearTimeout(trackPublicationsDebouncer);
		trackPublicationsDebouncer = setTimeout(() => {
			if ($room) {
				let t = {};
				$room.remoteParticipants.forEach((participant) => {
					participant.trackPublications.forEach((publication) => {

						// // 04 Feb 2025 -- disregard dummy tracks
						// if (publication.trackName && (publication.trackName == 'dummy')) {
						// 	return;
						// }

						if (publication.isSubscribed) {

							let i = participant.identity;

							if ((publication.source == 'screen_share') || (publication.source == 'screen_share_audio')) {
								i = 's_' + participant.identity;
							}

							const k = publication.kind;

							if (!t[i]) {
								t[i] = {};
							}

							t[i][k] = publication;

						}

					});
				});
				$trackPublications = t;
			}
		}, 100);
	}

	let libraryDebouncers = {};

	function refreshLibrary(kind) {
		clearTimeout(libraryDebouncers[kind]);
		libraryDebouncers[kind] = setTimeout(async () => {
			const newData = await getServerData('virtual/studio/' + kind, {
				sessionRef: $session.ref
			});
			if (kind == 'backgrounds') {
				$backgrounds = newData;
			} else if (kind == 'overlays') {
				$overlays = newData;
			} else if (kind == 'banners') {
				$banners = newData;
			} else if (kind == 'clips') {
				$clips = newData;
			}
		}, 100);
	}

	async function setStoresFromSync(syncData) {
		$participants = syncData.p ? syncData.p : [];
		$layout = syncData.l ? syncData.l : 'gap';
		$background = syncData.b ? syncData.b : null;
		$overlay = syncData.o ? syncData.o : null;
		$clip = syncData.c ? syncData.c : null;
		$banner = syncData.x ? syncData.x : null;
		$highlight = syncData.h ? syncData.h : null;
		$broadcasting = syncData.g ? syncData.g : null;
	}

	function countParticipants() {
		let c = 0;
		for (const p of $participants) {
			if (p.o) {
				c++;
			}
		}
		$participantCount = c;
	}

	async function getClipTokens(force) {
		if (force || mounted) {
			if (($event.setup.virtual.studio && $event.setup.virtual.studio.clips && $event.setup.virtual.studio.clips.length) || ($clips && $clips.length)) {
				const tokenData = await getServerData('virtual/studio/cliptokens', {
					sessionRef: $session.ref
				});
				$clipTokens = tokenData.clipTokens;
			}
		}
	}

	$: if (twilioSyncLoaded && syncToken) {
		twilioSyncConnect();
	}

	$: if ($syncClient && $session.syncChannel) {
		connectSessionChannels();
	}

	$: if (rtcClientLoaded && playerxLoaded && mediachromeLoaded && muxvideoLoaded && roomToken) {
		loadRoom();
	}

	$: if ($clips || $event) {
		getClipTokens();
	}

</script>

<style>
	.envelope {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		/*background: #000;*/
		z-index: 2;
	}
	.envelope.slate {
		z-index: 1;
	}
	.envelope.slate img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		aspect-ratio: 16/9;
		overflow: hidden;
	}

</style>

<svelte:head>
  	<script type="text/javascript" src="https://media.twiliocdn.com/sdk/js/sync/releases/3.0.1/twilio-sync.min.js" on:load={setTwilioSyncLoaded} on:error={() => scriptLoadError('twilio-sync')}></script>
  	<script type="module" src="https://cdn.jsdelivr.net/npm/playerx/+esm" on:load={() => {playerxLoaded = true}} on:error={() => scriptLoadError('playerx')}></script>
  	<script type="module" src="https://cdn.jsdelivr.net/npm/media-chrome@1/+esm" on:load={() => {mediachromeLoaded = true}} on:error={() => scriptLoadError('media-chrome')}></script>
  	<script type="module" src="https://cdn.jsdelivr.net/npm/@mux/mux-video@0/dist/mux-video.mjs" on:load={() => {muxvideoLoaded = true}} on:error={() => scriptLoadError('mux-video')}></script>
	{#each $backgrounds as image}
		{#if image.format == 'svg'}
			<link rel="preload" as="image" href="https://cdn.attendzen.io/{$event.accountRef}/{image.filename}" />
		{:else}
			<link rel="preload" as="image" href="https://cdn.attendzen.io/{$event.accountRef}/max_{image.filename}" />
		{/if}
	{/each}
	{#each $overlays as image}
		{#if image.format == 'svg'}
			<link rel="preload" as="image" href="https://cdn.attendzen.io/{$event.accountRef}/{image.filename}" />
		{:else}
			<link rel="preload" as="image" href="https://cdn.attendzen.io/{$event.accountRef}/max_{image.filename}" />
		{/if}
	{/each}
	{#if $event?.theme?.virtual?.slate?.filename}
		<link rel="preload" as="image" href="https://cdn.attendzen.io/{$event.accountRef}/max_{$event.theme.virtual.slate.filename}" />
	{/if}
</svelte:head>

{#if mounted && connected && ($livestreamStatus != 'idle')}
	{#if $broadcasting}
		<div class="envelope" in:fade={{ duration: 400 }} out:fade={{ duration: 200 }}>
			<Stage/>
		</div>
	{:else if $event?.theme?.virtual?.slate?.filename}
		<div class="envelope slate" in:fade={{ duration: 400 }} out:fade={{ duration: 200, delay: 800 }}>
			<img
				style="object-position: {$event.theme.virtual.slate.focalPointX}% {$event.theme.virtual.slate.focalPointY}%;"
				src="https://cdn.attendzen.io/{$event.accountRef}/max_{$event.theme.virtual.slate.filename}"
				alt=""
			/>
		</div>
	{/if}
	<!-- {#each $participants as participant}
		<HiddenParticipant {participant}/>
	{/each} -->
{/if}